import { CheckInEdit, type CheckInView } from "@pagerless/schema";
import { useState } from "react";
import { ErrorNote } from "../components/Shell";
import { useDeps, useResource } from "../context";
import { clock, dayClock } from "../lib/format";

/**
 * Scheduled jobs (check-ins) under an app on the Apps screen: name, last run, next due, state.
 * Jobs appear on their first check-in from the SDK; Edit changes the schedule here.
 */

const DAY = 86_400_000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** "3:31 am" today, "Fri 3:30 am" within a week, else "21 Nov 3:30 am". */
export function when(ms: number, nowMs: number): string {
  if (new Date(ms).toDateString() === new Date(nowMs).toDateString()) return clock(ms);
  if (Math.abs(ms - nowMs) < 6 * DAY) return dayClock(ms);
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${clock(ms)}`;
}

const UNITS: ReadonlyArray<[number, string]> = [
  [86_400, "day"],
  [3600, "hour"],
  [60, "minute"],
];

/** "every 6 hours", "every day", "at 30 3 * * *" */
export function scheduleText(v: Pick<CheckInView, "expectEverySeconds" | "cron" | "tz">): string {
  if (v.cron) return `at ${v.cron}${v.tz ? ` ${v.tz}` : ""}`;
  const s = v.expectEverySeconds;
  if (!s) return "no schedule";
  for (const [size, unit] of UNITS) {
    if (s % size === 0) {
      const n = s / size;
      return n === 1 ? `every ${unit}` : `every ${n} ${unit}s`;
    }
  }
  return `every ${s} seconds`;
}

/** One plain line: what happened last and what's next. */
export function jobLine(v: CheckInView, nowMs: number): string {
  const last = v.lastAt === null ? null : when(v.lastAt, nowMs);
  const next = v.nextDueAt === null ? null : when(v.nextDueAt, nowMs);
  switch (v.state) {
    case "late":
      return last ? `Didn't run · last ran ${last}` : "Didn't run · never ran";
    case "failed":
      return last ? `Failed ${last}` : "Failed";
    case "paused":
      return "Paused by you";
    case "unscheduled":
      return last ? `Ran ${last} · no schedule yet` : "No schedule yet";
    case "new":
      return next ? `Waiting for first run · due ${next}` : "Waiting for first run";
    case "ok":
      return [last ? `Ran ${last}` : null, next ? `next ${next}` : null].filter(Boolean).join(" · ");
  }
}

export function Jobs({ projectId }: { projectId: string }) {
  const { now } = useDeps();
  const jobs = useResource((a) => a.checkIns(projectId), projectId);
  const [editing, setEditing] = useState<string | null>(null);
  if (jobs.status === "loading") return null;
  if (jobs.status === "error")
    return (
      <li className="row job">
        <span />
        <ErrorNote error={jobs.error} retry={jobs.reload} />
      </li>
    );
  // Rows in the Apps list itself, indented under their app.
  return (
    <>
      {jobs.data.map((j) => (
        <li key={j.slug} className="row job" aria-label={`Scheduled job: ${j.name}`}>
          <span
            className={`dot${j.state === "late" || j.state === "failed" ? " down" : j.state === "ok" ? " up" : ""}`}
            aria-hidden="true"
          />
          {editing === j.slug ? (
            <JobForm
              job={j}
              projectId={projectId}
              done={() => {
                setEditing(null);
                jobs.reload();
              }}
            />
          ) : (
            <>
              <div>
                <b>{j.name}</b>
                <p className="muted">
                  {jobLine(j, now())} · {scheduleText(j)}
                </p>
              </div>
              <button type="button" className="btn alt sm" onClick={() => setEditing(j.slug)}>
                Edit
              </button>
            </>
          )}
        </li>
      ))}
    </>
  );
}

type Unit = "minutes" | "hours" | "days";
const UNIT_S: Record<Unit, number> = { minutes: 60, hours: 3600, days: 86_400 };

function everyParts(seconds: number | null): { n: string; unit: Unit } {
  if (!seconds) return { n: "1", unit: "days" };
  if (seconds % 86_400 === 0) return { n: String(seconds / 86_400), unit: "days" };
  if (seconds % 3600 === 0) return { n: String(seconds / 3600), unit: "hours" };
  return { n: String(Math.max(1, Math.round(seconds / 60))), unit: "minutes" };
}

/** Builds the edit from the form; null with a plain reason when it can't be saved. */
export function buildEdit(f: {
  kind: "every" | "cron";
  n: string;
  unit: Unit;
  cron: string;
  graceMin: string;
  level: "tell" | "wake";
  paused: boolean;
  name: string;
  /** The cron's zone as set before (the form doesn't change it). */
  tz: string | null;
}): { edit: CheckInEdit } | { error: string } {
  const edit: CheckInEdit = { name: f.name.trim() || undefined, level: f.level, paused: f.paused };
  if (f.kind === "every") {
    const n = Number(f.n);
    if (!Number.isInteger(n) || n < 1) return { error: "Say how often it runs, as a whole number." };
    edit.expectEverySeconds = n * UNIT_S[f.unit];
    if (edit.expectEverySeconds < 60) return { error: "Jobs can run at most once a minute." };
  } else {
    if (!f.cron.trim()) return { error: "Write the cron schedule, like 30 3 * * *." };
    edit.cron = f.cron.trim();
    if (f.tz) edit.tz = f.tz;
  }
  if (f.graceMin.trim()) {
    const g = Number(f.graceMin);
    if (!Number.isInteger(g) || g < 0) return { error: "Late after: a whole number of minutes." };
    edit.graceSeconds = g * 60;
  }
  const checked = CheckInEdit.safeParse(edit);
  if (!checked.success) {
    const why = checked.error.issues[0]?.message ?? "that schedule isn't valid";
    return { error: `${why[0]?.toUpperCase() ?? ""}${why.slice(1)}.` };
  }
  return { edit: checked.data };
}

function JobForm({ job, projectId, done }: { job: CheckInView; projectId: string; done: () => void }) {
  const { api } = useDeps();
  const every = everyParts(job.expectEverySeconds);
  const [f, setF] = useState({
    kind: (job.cron ? "cron" : "every") as "every" | "cron",
    n: every.n,
    unit: every.unit,
    cron: job.cron ?? "",
    graceMin: String(Math.round(job.graceSeconds / 60)),
    level: job.level,
    paused: job.paused,
    name: job.name,
    tz: job.tz,
  });
  const [state, setState] = useState<
    { s: "idle" | "saving" } | { s: "error"; error: Error } | { s: "invalid"; reason: string }
  >({ s: "idle" });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const built = buildEdit(f);
    if ("error" in built) return setState({ s: "invalid", reason: built.error });
    setState({ s: "saving" });
    try {
      await api.saveCheckIn(projectId, job.slug, built.edit);
      done();
    } catch (err) {
      setState({ s: "error", error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  const id = `job-${job.slug}`;
  return (
    <form onSubmit={save} aria-label={`Edit ${job.name}`}>
      <label className="label" htmlFor={`${id}-name`}>
        Name
      </label>
      <input id={`${id}-name`} className="field" value={f.name} onChange={(e) => set("name", e.target.value)} />
      <label className="label" htmlFor={`${id}-kind`}>
        Runs
      </label>
      <select
        id={`${id}-kind`}
        className="field"
        value={f.kind}
        onChange={(e) => set("kind", e.target.value === "cron" ? "cron" : "every")}
      >
        <option value="every">Every…</option>
        <option value="cron">On a cron schedule</option>
      </select>
      {f.kind === "every" ? (
        <div className="btn-row">
          <input
            aria-label="How many"
            className="field"
            inputMode="numeric"
            value={f.n}
            onChange={(e) => set("n", e.target.value)}
          />
          <select
            aria-label="Unit"
            className="field"
            value={f.unit}
            onChange={(e) => set("unit", e.target.value as Unit)}
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
        </div>
      ) : (
        <input
          aria-label="Cron schedule"
          className="field"
          placeholder="30 3 * * *"
          value={f.cron}
          onChange={(e) => set("cron", e.target.value)}
        />
      )}
      <label className="label" htmlFor={`${id}-grace`}>
        Late after (minutes)
      </label>
      <input
        id={`${id}-grace`}
        className="field"
        inputMode="numeric"
        value={f.graceMin}
        onChange={(e) => set("graceMin", e.target.value)}
      />
      <label className="label" htmlFor={`${id}-level`}>
        If it misses a run or fails
      </label>
      <select
        id={`${id}-level`}
        className="field"
        value={f.level}
        onChange={(e) => set("level", e.target.value === "wake" ? "wake" : "tell")}
      >
        <option value="tell">Tell me</option>
        <option value="wake">Wake me up</option>
      </select>
      <label className="check">
        <input type="checkbox" checked={f.paused} onChange={(e) => set("paused", e.target.checked)} />
        <span>Pause: don't tell me about missed runs</span>
      </label>
      <div className="btn-row">
        <button type="submit" className="btn sm" disabled={state.s === "saving"}>
          {state.s === "saving" ? "Saving…" : "Save"}
        </button>
        <button type="button" className="btn alt sm" onClick={done}>
          Cancel
        </button>
      </div>
      <div aria-live="polite">
        {state.s === "error" && <ErrorNote error={state.error} />}
        {state.s === "invalid" && (
          <p className="err" role="alert">
            {state.reason}
          </p>
        )}
      </div>
    </form>
  );
}
